/**
 * LocalizedInput.tsx — multi-language input rows.
 * loc-input-row, loc-tag, loc-fallback (see app.css).
 */

import React from 'react';
import { View, Text, Platform, ViewStyle } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Input, Textarea, Icon } from '../../components/ui';
import { SK } from '../../data/mockData';
import type { FieldDef } from '../../data/mockData';

interface LocalizedInputProps {
  field: FieldDef;
  value: Record<string, string> | undefined;
  rich?: boolean;
  onChange: (v: Record<string, string>) => void;
}

export default function LocalizedInput({ field, value, rich, onChange }: LocalizedInputProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const val = value || {};

  return (
    <View>
      {SK.languages.map(lang => {
        const has = val[lang.code] != null && val[lang.code] !== '';
        const isDefault = !!lang.isDefault;

        // loc-tag styles
        const tagContainerStyle: ViewStyle = {
          flexShrink: 0,
          width: 64,
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: 1,
          borderColor: isDefault ? colors.accentLine : colors.line,
          borderRadius: radii.r2,
          backgroundColor: isDefault ? colors.accentSoft : colors.paper2,
          minHeight: 34,
          alignSelf: 'stretch',
        };

        return (
          <View
            key={lang.code}
            style={{
              flexDirection: 'row',
              alignItems: 'stretch',
              gap: 8,
              marginBottom: 8,
            }}
          >
            {/* loc-tag */}
            <View style={tagContainerStyle}>
              <Text style={{
                fontFamily: font.mono,
                fontSize: fontSize.xs,
                color: isDefault ? colors.accentInk : colors.ink3,
                fontWeight: fontWeight.medium,
              }}>
                {lang.short}
              </Text>
            </View>

            {/* input area */}
            <View style={{ flex: 1 }}>
              {rich ? (
                <Textarea
                  value={val[lang.code] || ''}
                  minHeight={56}
                  placeholder={isDefault ? 'Default content…' : has ? '' : 'Falls back to English'}
                  onChangeText={v => onChange({ ...val, [lang.code]: v })}
                />
              ) : (
                <Input
                  value={val[lang.code] || ''}
                  placeholder={isDefault ? 'Default…' : has ? '' : 'Falls back to English'}
                  onChangeText={v => onChange({ ...val, [lang.code]: v })}
                />
              )}
              {/* loc-fallback */}
              {!isDefault && !has && (
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  marginTop: 3,
                }}>
                  <Icon name="info" size={11} color={colors.warn} />
                  <Text style={{
                    fontSize: fontSize.xs,
                    color: colors.warn,
                    fontFamily: font.sans,
                  }}>
                    Missing — falls back to English default
                  </Text>
                </View>
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
}
